import { Plus, Trash2, MoreVertical, Columns3, Rows3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { Block, TableBlockContent } from '@/types';

interface TableBlockProps {
  block: Block;
  isEditable?: boolean;
  onUpdate?: (patch: Partial<Block>) => void;
}

function TableBlock({ block, isEditable = false, onUpdate }: TableBlockProps) {
  const content = block.content as TableBlockContent;

  const headers = content.headers || ['Kolom 1', 'Kolom 2', 'Kolom 3'];
  const rows = content.rows || [
    ['', '', ''],
    ['', '', ''],
  ];

  const updateHeaders = (newHeaders: string[]) => {
    onUpdate?.({
      content: { ...content, headers: newHeaders },
    });
  };

  const updateCell = (rowIndex: number, colIndex: number, value: string) => {
    const newRows = rows.map((row, rIdx) =>
      rIdx === rowIndex
        ? row.map((cell, cIdx) => (cIdx === colIndex ? value : cell))
        : row
    );
    onUpdate?.({
      content: { ...content, rows: newRows },
    });
  };

  const addRow = (afterIndex?: number) => {
    const newRow = headers.map(() => '');
    const insertIndex = afterIndex !== undefined ? afterIndex + 1 : rows.length;
    const newRows = [...rows.slice(0, insertIndex), newRow, ...rows.slice(insertIndex)];
    onUpdate?.({
      content: { ...content, rows: newRows },
    });
  };

  const removeRow = (index: number) => {
    if (rows.length <= 1) return;
    const newRows = rows.filter((_, i) => i !== index);
    onUpdate?.({
      content: { ...content, rows: newRows },
    });
  };

  const addColumn = (afterIndex?: number) => {
    const insertIndex = afterIndex !== undefined ? afterIndex + 1 : headers.length;
    const newHeaders = [
      ...headers.slice(0, insertIndex),
      `Kolom ${headers.length + 1}`,
      ...headers.slice(insertIndex),
    ];
    const newRows = rows.map((row) => [
      ...row.slice(0, insertIndex),
      '',
      ...row.slice(insertIndex),
    ]);
    onUpdate?.({
      content: { ...content, headers: newHeaders, rows: newRows },
    });
  };

  const removeColumn = (index: number) => {
    if (headers.length <= 1) return;
    const newHeaders = headers.filter((_, i) => i !== index);
    const newRows = rows.map((row) => row.filter((_, i) => i !== index));
    onUpdate?.({
      content: { ...content, headers: newHeaders, rows: newRows },
    });
  };

  if (isEditable) {
    return (
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100">
                {headers.map((header, colIndex) => (
                  <th key={colIndex} className="relative min-w-[120px] px-3 py-2 text-left">
                    <div className="flex items-center gap-1">
                      <input
                        value={header}
                        onChange={(e) => {
                          const newHeaders = [...headers];
                          newHeaders[colIndex] = e.target.value;
                          updateHeaders(newHeaders);
                        }}
                        className="w-full bg-transparent border-none outline-none font-semibold text-sm"
                      />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onClick={() => addColumn(colIndex)}>
                            <Columns3 className="h-4 w-4 mr-2" />
                            Kolom invoegen rechts
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => removeColumn(colIndex)}
                            disabled={headers.length <= 1}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Kolom verwijderen
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </th>
                ))}
                <th className="w-[50px] bg-gray-100 px-2">
                  <Button variant="ghost" size="icon-sm" onClick={() => addColumn()} title="Kolom toevoegen">
                    <Plus className="h-4 w-4" />
                  </Button>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="hover:bg-gray-50 border-t border-border">
                  {row.map((cell, colIndex) => (
                    <td key={colIndex} className="px-3 py-1">
                      <input
                        value={cell}
                        onChange={(e) => updateCell(rowIndex, colIndex, e.target.value)}
                        className="w-full bg-transparent border-none outline-none text-sm"
                        placeholder="..."
                      />
                    </td>
                  ))}
                  <td className="px-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon-sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem onClick={() => addRow(rowIndex)}>
                          <Rows3 className="h-4 w-4 mr-2" />
                          Rij invoegen onder
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => removeRow(rowIndex)}
                          disabled={rows.length <= 1}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Rij verwijderen
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="p-2 border-t border-border bg-gray-50">
          <Button variant="ghost" size="sm" onClick={() => addRow()}>
            <Rows3 className="h-4 w-4 mr-1" />
            Rij toevoegen
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-100">
            {headers.map((header, index) => (
              <th key={index} className="px-3 py-2 text-left font-semibold">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className="border-t border-border">
              {row.map((cell, colIndex) => (
                <td key={colIndex} className="px-3 py-2">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default TableBlock;
